import { useState } from 'react';
import { Card, GoldBtn, ImageLightbox, SectionLabel } from '@/components/ui';
import { features } from '@/content/features';
import { gmTools } from '@/content/gmTools';
import { homebrewItems } from '@/content/homebrew';
import { assets, C, externalLinks } from '@/theme/tokens';
import { scrollToReserve, scrollToDemo } from '@/utils/scroll';
import { useIsMobile } from '@/hooks/isMobile';

/* ------------------------------------------------------------------ */
/*  Tab definitions                                                    */
/* ------------------------------------------------------------------ */

const TABS = ['Players', 'Game Masters', 'Homebrew Creators'] as const;
type Tab = (typeof TABS)[number];

const TAB_ICONS: Record<Tab, string> = {
  'Players': '⚔',
  'Game Masters': '🗺',
  'Homebrew Creators': '🛠',
};

/* ------------------------------------------------------------------ */
/*  Sub-panels                                                         */
/* ------------------------------------------------------------------ */

/** "For Players" — intro copy + grid of bordered feature blurbs (migrated from FeaturesSection). */
function PlayersPanel() {
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const isMobile = useIsMobile();
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
        gap: 48,
        alignItems: 'start',
      }}
    >
      {/* Left column: text + feature cards */}
      <div>
        {/* Top: intro copy */}
        <div style={{ maxWidth: 720, marginBottom: 48 }}>
          <h3
            style={{
              fontFamily: "'Cinzel',serif",
              fontSize: 'clamp(1.4rem,3.5vw,2rem)',
              fontWeight: 700,
              letterSpacing: '1.5px',
              color: '#fff',
              textTransform: 'uppercase',
              marginBottom: 16,
              lineHeight: 1.2,
            }}
          >
            Your Adventure.
            <br />
            <span style={{ color: C.gold }}>Your Way.</span>
          </h3>
          <p
            style={{
              fontSize: 15,
              color: 'rgba(255,255,255,0.6)',
              lineHeight: 1.75,
              marginBottom: 28,
              fontWeight: 300,
            }}
          >
            QuestXR drops you into real-world Quests powered by the legendary BRP rules system, augmented reality, and
            multi-media. Experience on-location encounters, battle creatures only you and your party can see, and level up a character that's truly yours.
          </p>
          <p
            style={{
              fontSize: 14,
              color: 'rgba(255,255,255,0.45)',
              lineHeight: 1.7,
              fontWeight: 300,
              marginBottom: 32,
            }}
          >
            Whether you're a seasoned tabletop veteran or brand-new to RPGs, QuestXR is designed to
            feel intuitive from the first encounter — and endlessly deep for those who seek mastery.
          </p>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', justifyContent: 'center'  }}>
            <GoldBtn onClick={scrollToReserve}>Reserve Your Spot</GoldBtn>
            <GoldBtn variant="outlined" onClick={scrollToDemo}>
              See it in Action
            </GoldBtn>
          </div>
        </div>

        {/* Feature grid */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
            gap: 20,
          }}
        >
          {features.map(({ title, desc }) => (
            <div
              key={title}
              style={{
                padding: '24px 24px',
                borderLeft: `3px solid ${C.goldDark}`,
                background: 'rgba(10,26,42,0.5)',
                borderRadius: '0 5px 5px 0',
                transition: 'all 0.2s',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderLeftColor = C.gold;
                e.currentTarget.style.background = 'rgba(10,26,42,0.9)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderLeftColor = C.goldDark;
                e.currentTarget.style.background = 'rgba(10,26,42,0.5)';
              }}
            >
              <h4
                style={{
                  fontFamily: "'Cinzel',serif",
                  fontSize: 14,
                  fontWeight: 700,
                  letterSpacing: '1px',
                  color: C.gold,
                  textTransform: 'uppercase',
                  marginBottom: 8,
                }}
              >
                {title}
              </h4>
              <p
                style={{
                  fontSize: 13,
                  color: 'rgba(255,255,255,0.6)',
                  lineHeight: 1.65,
                  fontWeight: 300,
                }}
              >
                {desc}
              </p>
            </div>
          ))}
        </div>
      </div>

      {/* Right column: character sheet image */}
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          marginBottom: isMobile ? '-40px' : '0',
        }}
      >
        <img
          src="/assets/chrsheetp1.png"
          alt="Character Sheet — tap to view"
          onClick={() => setLightboxOpen(true)}
          style={{
            width: '79%',
            objectFit: 'contain',
            borderRadius: 8,
            cursor: 'zoom-in',
            transition: 'transform 0.2s ease, box-shadow 0.2s ease',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'scale(1.02)';
            e.currentTarget.style.boxShadow = '0 0 24px rgba(212,175,55,0.25)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'scale(1)';
            e.currentTarget.style.boxShadow = 'none';
          }}
        />
        <div style={{margin: '12px 0'}}><i>Click/tap to expand</i></div>
      </div>

      {/* Fullscreen lightbox */}
      {lightboxOpen && (
        <ImageLightbox
          src="/assets/charactersheet.png"
          alt="Character Sheet — Full Size"
          onClose={() => setLightboxOpen(false)}
        />
      )}
    </div>
  );
}

/** "For Game Masters" — split layout with copy + tool cards (migrated from GameMasterSection). */
function GameMasterPanel() {
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const isMobile = useIsMobile();
  return (
    <>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
          gap: 64,
          alignItems: 'center',
        }}
      >
        {/* Left: copy */}
        <div>
          <h3
            style={{
              fontFamily: "'Cinzel',serif",
              fontSize: 'clamp(1.4rem,3.5vw,2rem)',
              fontWeight: 700,
              letterSpacing: '1.5px',
              color: '#fff',
              textTransform: 'uppercase',
              marginBottom: 16,
              lineHeight: 1.2,
            }}
          >
            Create Quests.
            <br />
            <span style={{ color: C.gold }}>Earn From Your World.</span>
          </h3>
          <p
            style={{
              fontSize: 15,
              color: 'rgba(255,255,255,0.6)',
              lineHeight: 1.75,
              marginBottom: 28,
              fontWeight: 300,
            }}
          >
            QuestXR's Quest Creation Toolkit puts the full power of adventure design in your hands.
            Build immersive Quests using the BRP rules system, then use them to enhance your home game, send your players on side quests, and/or publish them to
            players worldwide and earn each time players run your Quest.
          </p>
          <p
            style={{
              fontSize: 14,
              color: 'rgba(255,255,255,0.45)',
              lineHeight: 1.7,
              fontWeight: 300,
              marginBottom: 32,
            }}
          >
            Whether you're an experienced Game Master or a first-time storyteller, the toolkit is
            designed to make the complex simple — and the simple extraordinary.
          </p>
          <div style={{ marginBottom: '16px', marginTop: '-12px' }}>
            <img
              src="/assets/gmi.png"
              alt="Game Master Interface — click to expand"

              onClick={() => setLightboxOpen(true)}
              style={{
                cursor: 'zoom-in',
                transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                maxWidth: '100%',
                height: 'auto',
                marginRight: '12px'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'scale(1.02)';
                e.currentTarget.style.boxShadow = '0 0 24px rgba(212,175,55,0.25)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'scale(1)';
                e.currentTarget.style.boxShadow = 'none';
              }}
            />
          </div>
          <div style={{ textAlign: 'center', marginTop: '-16px', marginBottom: '12px', fontSize: '12px' }}><i>(Tap to View)</i></div>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', justifyContent: 'center' }}>
            <GoldBtn style={{fontSize: isMobile ? '12px' :'14px' }} href={externalLinks.backerKit}>Reserve Your Spot</GoldBtn>
        {/* <GoldBtn variant="outlined" href={externalLinks.demo}>
              See it in Action
            </GoldBtn> */}
          </div>
        </div>

        {/* Right: feature cards */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
          {gmTools.map(({ title, desc, icon }/*, i*/) => {
            // const isLast = i === gmTools.length - 1;
            return (
              <Card key={title} style={{ padding: '24px 20px' }}>
                <div
                  style={{
                    width: 32,
                    height: 32,
                    borderRadius: '50%',
                    marginBottom: 14,
                    background: 'rgba(212,175,55,0.12)',
                    border: '1px solid rgba(212,175,55,0.3)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 14,
                  }}
                >
                  {icon}
                </div>
                <h4
                  style={{
                    fontFamily: "'Cinzel',serif",
                    fontSize: 12,
                    fontWeight: 700,
                    letterSpacing: '1px',
                    color: '#fff',
                    textTransform: 'uppercase',
                    marginBottom: 8,
                  }}
                >
                  {title}
                </h4>
                <p
                  style={{
                    fontSize: 12,
                    color: 'rgba(255,255,255,0.55)',
                    lineHeight: 1.65,
                    fontWeight: 300,
                  }}
                >
                  {desc}
                </p>
              </Card>
            );
          })}
        </div>
      </div>

      {/* BRP compatibility callout bar */}
      <div
        style={{
          marginTop: 56,
          padding: '24px 32px',
          background: 'rgba(20,38,59,0.7)',
          border: '1px solid rgba(193,154,107,0.35)',
          borderRadius: 5,
          display: 'flex',
          alignItems: 'center',
          gap: 24,
          flexWrap: 'wrap',
          justifyContent: 'space-between',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <div
            style={{
              width: 48,
              height: 48,
              borderRadius: '50%',
              flexShrink: 0,
              background: 'linear-gradient(135deg,#D4AF37,#C19A6B)',
              display: isMobile ? 'none' : 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontFamily: "'Cinzel',serif",
              fontSize: 13,
              fontWeight: 900,
              color: '#0a1a2a',
            }}
          >
            <img
              src={assets.brpicon}
              alt="QuestXR"
              style={{ height: 36, objectFit: 'contain' }}
            />
          </div>
          <div>
            <div
              style={{
                fontFamily: "'Cinzel',serif",
                fontSize: 14,
                fontWeight: 700,
                color: C.gold,
                letterSpacing: '1px',
                textTransform: 'uppercase',
                marginBottom: 4,
              }}
            >
              Built on Chaosium's Basic Roleplaying System
            </div>
            <div
              style={{
                fontSize: 13,
                color: 'rgba(255,255,255,0.55)',
                fontWeight: 300,
              }}
            >
              QuestXR uses the same proven ruleset that powers <em>Call of Cthulhu</em>,{' '}
              <em>RuneQuest</em>, and decades of beloved tabletop adventures. Your quests are fully
              BRP-compatible.  With more systems to come.
            </div>
            <GoldBtn
              variant="outlined"
              size="sm"
              href={externalLinks.brpInfo}
              style={{ whiteSpace: 'nowrap', marginTop: '12px' }}
            >
              About BRP ↗
            </GoldBtn>
          </div>
        </div>
      </div>

      {/* Fullscreen lightbox */}
      {lightboxOpen && (
        <ImageLightbox
          src="/assets/gmi.png"
          alt="Game Master Interface — Full Size"
          onClose={() => setLightboxOpen(false)}
        />
      )}
    </>
  );
}

/** "Homebrew Creators" — split layout matching GM style, with homebrew content cards. */
function HomebrewPanel() {
  const [lightboxOpen, setLightboxOpen] = useState(false);

  return (
    <>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
          gap: 64,
          alignItems: 'center',
        }}
      >
        {/* Left: copy */}
        <div>
          <h3
            style={{
              fontFamily: "'Cinzel',serif",
              fontSize: 'clamp(1.4rem,3.5vw,2rem)',
              fontWeight: 700,
              letterSpacing: '1.5px',
              color: '#fff',
              textTransform: 'uppercase',
              marginBottom: 16,
              lineHeight: 1.2,
            }}
          >
            Build the World.
            <br />
            <span style={{ color: C.gold }}>Share & Earn.</span>
          </h3>
          <p
            style={{
              fontSize: 15,
              color: 'rgba(255,255,255,0.6)',
              lineHeight: 1.75,
              marginBottom: 28,
              fontWeight: 300,
            }}
          >
            QuestXR isn't just a game — it's a platform. Create original creatures, items, spells,
            and skills using our Homebrew tools, then publish them for the entire community. The best
            homebrew content gets featured, rated, and used in Quests around the world.
          </p>
          <p
            style={{
              fontSize: 14,
              color: 'rgba(255,255,255,0.45)',
              lineHeight: 1.7,
              fontWeight: 300,
              marginBottom: 32,
            }}
          >
            Whether you're theory-crafting a legendary weapon or designing the next great dungeon
            boss, the Homebrew toolkit gives you full creative freedom — and a way to get paid for
            your imagination.
          </p>
                <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          margin: '-12px auto',
        }}
      >
        <img
          src="/assets/homebrew-strip-h.png"
          alt="Homebrew Content — tap to view"
          onClick={() => setLightboxOpen(true)}
          style={{
            width: '99%',
            objectFit: 'contain',
            borderRadius: 8,
            cursor: 'zoom-in',
            transition: 'transform 0.2s ease, box-shadow 0.2s ease',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'scale(1.02)';
            e.currentTarget.style.boxShadow = '0 0 24px rgba(212,175,55,0.25)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'scale(1)';
            e.currentTarget.style.boxShadow = 'none';
          }}
        />
      </div>
      <div style={{ textAlign: 'center', marginTop: '20px', marginBottom: '24px', fontSize: '12px' }}><i>(Tap to View)</i></div>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', justifyContent: 'center' }}>
            <GoldBtn onClick={scrollToReserve}>Reserve Your Spot</GoldBtn>
          </div>
        </div>

        {/* Right: homebrew content cards */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
          {homebrewItems.map(({ title, desc, icon }/*, i*/) => {
            // const isLast = i === homebrewItems.length - 1;
            return (
              <Card key={title} style={{ padding: '24px 20px' }}>
                <div
                  style={{
                    width: 32,
                    height: 32,
                    borderRadius: '50%',
                    marginBottom: 14,
                    background: 'rgba(212,175,55,0.12)',
                    border: '1px solid rgba(212,175,55,0.3)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 14,
                  }}
                >
                  {icon}
                </div>
                <h4
                  style={{
                    fontFamily: "'Cinzel',serif",
                    fontSize: 12,
                    fontWeight: 700,
                    letterSpacing: '1px',
                    color: '#fff',
                    textTransform: 'uppercase',
                    marginBottom: 8,
                  }}
                >
                  {title}
                </h4>
                <p
                  style={{
                    fontSize: 12,
                    color: 'rgba(255,255,255,0.55)',
                    lineHeight: 1.65,
                    fontWeight: 300,
                  }}
                >
                  {desc}
                </p>
              </Card>
            );
          })}
        </div>
      </div>

      {/* Monetization callout bar */}
      <div
        style={{
          marginTop: 56,
          padding: '24px 32px',
          background: 'rgba(20,38,59,0.7)',
          border: '1px solid rgba(193,154,107,0.35)',
          borderRadius: 5,
          display: 'flex',
          alignItems: 'center',
          gap: 24,
          flexWrap: 'wrap',
          justifyContent: 'space-between',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <div
            style={{
              width: 48,
              height: 48,
              borderRadius: '50%',
              flexShrink: 0,
              background: 'linear-gradient(135deg,#D4AF37,#C19A6B)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 22,
            }}
          >
            💰
          </div>
          <div>
            <div
              style={{
                fontFamily: "'Cinzel',serif",
                fontSize: 14,
                fontWeight: 700,
                color: C.gold,
                letterSpacing: '1px',
                textTransform: 'uppercase',
                marginBottom: 4,
              }}
            >
              Creator Revenue Program
            </div>
            <div
              style={{
                fontSize: 13,
                color: 'rgba(255,255,255,0.55)',
                fontWeight: 300,
              }}
            >
              Publish premium homebrew content and earn revenue from every download. Set your own
              prices, build a catalog, and grow a following — all within the QuestXR ecosystem.
            </div>
              <GoldBtn
                variant="outlined"
                size="sm"
                onClick={scrollToReserve}
                style={{ whiteSpace: 'nowrap', marginTop: '12px' }}
              >
                Learn More at Launch
              </GoldBtn>
          </div>
        </div>
      </div>

      {/* Fullscreen lightbox */}
      {lightboxOpen && (
        <ImageLightbox
          src="/assets/homebrew-strip-h-specs.png"
          alt="Homebrew Content — Full Size"
          onClose={() => setLightboxOpen(false)}
        />
      )}
    </>
  );
}

/* ------------------------------------------------------------------ */
/*  Tab bar component                                                  */
/* ------------------------------------------------------------------ */

function TabBar({
  active,
  onChange,
}: {
  active: Tab;
  onChange: (tab: Tab) => void;
}) {
  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'center',
        gap: 8,
        flexWrap: 'wrap',
        marginBottom: 56,
      }}
    >
      {TABS.map((tab) => {
        const isActive = tab === active;
        return (
          <button
            key={tab}
            type="button"
            onClick={() => onChange(tab)}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 8,
              padding: '10px 24px',
              borderRadius: 30,
              fontFamily: "'Cinzel',serif",
              fontSize: 13,
              fontWeight: 700,
              letterSpacing: '1.5px',
              textTransform: 'uppercase',
              cursor: 'pointer',
              transition: 'all 0.25s',
              border: isActive
                ? `1px solid ${C.gold}`
                : '1px solid rgba(193,154,107,0.35)',
              background: isActive
                ? 'rgba(212,175,55,0.12)'
                : 'rgba(10,26,42,0.5)',
              color: isActive ? C.gold : 'rgba(255,255,255,0.5)',
              boxShadow: isActive
                ? '0 0 20px rgba(212,175,55,0.15)'
                : 'none',
            }}
            onMouseEnter={(e) => {
              if (!isActive) {
                e.currentTarget.style.borderColor = C.bronze;
                e.currentTarget.style.color = C.bronze;
                e.currentTarget.style.background = 'rgba(212,175,55,0.06)';
              }
            }}
            onMouseLeave={(e) => {
              if (!isActive) {
                e.currentTarget.style.borderColor = 'rgba(193,154,107,0.35)';
                e.currentTarget.style.color = 'rgba(255,255,255,0.5)';
                e.currentTarget.style.background = 'rgba(10,26,42,0.5)';
              }
            }}
          >
            <span style={{ fontSize: 16 }}>{TAB_ICONS[tab]}</span>
            {tab}
          </button>
        );
      })}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Main section export                                                */
/* ------------------------------------------------------------------ */

/**
 * Consolidated "Explore" section that merges the old Features, Game Master,
 * and new Homebrew content into a single tabbed layout. Reduces vertical
 * scroll by ~2 full-height sections while adding Homebrew as a peer-level tab.
 */
export function ExploreSection() {
  const [activeTab, setActiveTab] = useState<Tab>('Players');

  return (
    <section
      id="explore"
      style={{ background: '#14263b', padding: '36px 24px' }}
    >
      <div style={{ maxWidth: 1100, margin: '0 auto' }}>
        {/* Section header */}
        <div style={{ textAlign: 'center', marginBottom: 16 }}>
          <SectionLabel>Explore QuestXR</SectionLabel>
          <h2
            style={{
              fontFamily: "'Cinzel',serif",
              fontSize: 'clamp(1.6rem,4vw,2.3rem)',
              fontWeight: 700,
              letterSpacing: '1.5px',
              color: '#fff',
              textTransform: 'uppercase',
              marginBottom: 12,
            }}
          >
            Built for <span style={{ color: C.gold }}>Adventurers, Creators</span> <br />&{' '}
            <span style={{ color: C.gold }}>World-Builders</span>
          </h2>
          <p
            style={{
              maxWidth: 560,
              margin: '0 auto',
              color: 'rgba(255,255,255,0.55)',
              fontSize: 15,
              lineHeight: 1.7,
            }}
          >
            Whether you play, run games, or build content — QuestXR has something for you.
          </p>
        </div>

        {/* Tab bar */}
        <TabBar active={activeTab} onChange={setActiveTab} />

        {/* Panel content — keyed so React remounts for a clean transition */}
        <div
          key={activeTab}
          style={{
            animation: 'fadeIn 0.35s ease',
          }}
        >
          {activeTab === 'Players' && <PlayersPanel />}
          {activeTab === 'Game Masters' && <GameMasterPanel />}
          {activeTab === 'Homebrew Creators' && <HomebrewPanel />}
        </div>
      </div>
    </section>
  );
}
